import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Shipment } from '../../shipment/models/shipment';
import {
    ShipmentContainerManifestCommand,
    ShipmentPayloadLineCommand,
    ShipmentPayloadLineCommandResult,
    ShipmentService
} from '../../shipment/shipment.service';
import { ShipmentIntakeService } from '../../shipment/shipment-intake.service';
import { ShipmentIntakeWarehouseItemOption } from '../../shipment/models/intake/shipment-intake.model';
import { ContainerTrackingService } from '../../container-tracking/container-tracking.service';
import { ContainerJourneyTemplateView } from '../../container-tracking/models/container-journey-plan.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { PayloadItemView } from '../../models/meta-params/payload-item-view';
import { PayloadType } from '../../models/meta-params/payload-type';
import { QuantityView } from '../../models/meta-params/quantity-view';
import { UOM } from '../../models/meta-params/uom';
import { Weight } from '../../models/meta-params/weight';

type PayloadSource = 'GENERAL_CARGO' | 'WAREHOUSE_ITEM' | 'CONTAINER';

interface JourneyTemplateOption {
    label: string;
    value: number;
}

@Component({
    selector: 'app-payload-line-command-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DialogModule,
        ButtonModule,
        InputTextModule,
        InputNumberModule,
        SelectModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        TextareaModule
    ],
    templateUrl: './payload-line-command-dialog.component.html',
    styleUrls: ['./payload-line-command-dialog.component.css']
})
export class PayloadLineCommandDialogComponent {
    private readonly shipmentService = inject(ShipmentService);
    private readonly intakeService = inject(ShipmentIntakeService);
    private readonly containerTrackingService = inject(ContainerTrackingService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly messageService = inject(MessageService);

    @Input({ required: true }) shipmentId?: number;
    @Input() deliveryId?: number;
    @Input() shipment?: Shipment;
    @Input() payloadTypes: PayloadType[] = [];
    @Input() uomList: UOM[] = [];
    @Input() weightUnits: Weight[] = [];
    @Input() disabled = false;
    @Output() changed = new EventEmitter<ShipmentPayloadLineCommandResult>();

    visible = false;
    saving = false;
    submitted = false;
    editing?: PayloadItemView;
    source: PayloadSource = 'GENERAL_CARGO';
    errorMessage = '';
    warehouseItems: ShipmentIntakeWarehouseItemOption[] = [];
    private warehouseUomCache = new WeakMap<ShipmentIntakeWarehouseItemOption, UOM[]>();
    warehouseItemsLoading = false;
    journeyTemplates: JourneyTemplateOption[] = [];
    journeyTemplatesLoading = false;

    form: {
        itemType?: PayloadType;
        description?: string;
        quantity?: number;
        uom?: UOM;
        itemWeight?: number;
        weightUnit?: Weight;
        warehouseItem?: ShipmentIntakeWarehouseItemOption;
        containerNumber?: string;
        isoEquipmentType?: string;
        sealNumber?: string;
        bookingNumber?: string;
        billOfLadingNumber?: string;
        houseBillNumber?: string;
        customsDeclarationNumber?: string;
        journeyTemplateId?: number;
    } = {};

    readonly sources: Array<{ value: PayloadSource; label: string; icon: string }> = [
        { value: 'GENERAL_CARGO', label: 'General Cargo', icon: 'fa-box' },
        { value: 'WAREHOUSE_ITEM', label: 'Warehouse Item', icon: 'fa-warehouse' },
        { value: 'CONTAINER', label: 'Container', icon: 'fa-container-storage' }
    ];

    readonly equipmentTypes = [
        '20GP', '40GP', '40HC', '45HC', '20RF', '40RF', 'TANK', 'FLAT_RACK', 'OPEN_TOP'
    ].map(value => ({ label: value, value }));

    openCreate(): void {
        if (this.disabled || !this.shipmentId) return;
        this.editing = undefined;
        this.source = 'GENERAL_CARGO';
        this.form = {};
        this.open();
    }

    openEdit(item: PayloadItemView): void {
        if (this.disabled || !this.shipmentId) return;
        this.editing = item;
        this.source = item.inventoryItemId ? 'WAREHOUSE_ITEM' : 'GENERAL_CARGO';
        this.form = {
            itemType: this.matchPayloadType(item.itemType),
            description: item.itemDescription || item.itemDesciption,
            quantity: item.enteredQuantity ?? item.itemQuantity?.quantity,
            uom: item.tenantUomId ? undefined : this.matchUom(item.itemQuantity?.uom),
            itemWeight: item.itemWeight,
            weightUnit: this.matchWeight(item.weightUnit)
        };
        this.open();
    }

    confirmDelete(item: PayloadItemView): void {
        if (this.disabled || !this.shipmentId || !item.shipmentLineId) return;
        this.confirmationService.confirm({
            header: 'Delete payload line',
            message: `Delete ${this.payloadDescription(item)}?`,
            icon: 'pi pi-trash',
            accept: () => this.delete(item)
        });
    }

    close(): void {
        if (!this.saving) this.visible = false;
    }

    chooseSource(source: PayloadSource): void {
        if (this.editing && source === 'CONTAINER') return;
        this.source = source;
        this.errorMessage = '';
        if (source === 'WAREHOUSE_ITEM') this.loadWarehouseItems();
        if (source === 'CONTAINER') this.loadJourneyTemplates();
    }

    onWarehouseItemChange(item?: ShipmentIntakeWarehouseItemOption): void {
        this.form.warehouseItem = item;
        if (!item) return;
        this.form.description = item.itemDescription || item.itemName;
        this.form.itemWeight = item.weightKg;
        this.form.uom = (item.defaultTenantUomId != null
            ? this.warehouseUoms().find(uom => this.tenantUomId(uom) === item.defaultTenantUomId) : undefined)
            || this.warehouseUoms().find(uom => Number(uom.uomId) === Number(item.defaultUomId))
            || this.warehouseUoms()[0];
    }

    warehouseUoms(): UOM[] {
        const item = this.form.warehouseItem
            || (this.editing?.inventoryItemId ? this.warehouseItems.find(option => option.inventoryItemId === this.editing?.inventoryItemId) : undefined);
        if (item?.tenantUomOptions?.length) {
            const cached = this.warehouseUomCache.get(item);
            if (cached) return cached;
            const tenantOptions: UOM[] = item.tenantUomOptions.map(option => Object.assign(new UOM(), {
                uomId: option.tenantUomId,
                name: option.name,
                shortName: option.packLabel || option.name,
                tenantUomId: option.tenantUomId
            }));
            const legacy = this.editing && !this.editing.tenantUomId && this.editing.itemQuantity?.uom;
            if (legacy && item.compatibleUomIds?.includes(Number(legacy.uomId))) {
                const globalUom = this.matchUom(legacy);
                if (globalUom) tenantOptions.push(globalUom);
            }
            this.warehouseUomCache.set(item, tenantOptions);
            return tenantOptions;
        }
        if (item?.defaultTenantUomId != null) return [];
        if (!item?.compatibleUomIds?.length) {
            const each = this.uomList.find(uom => /(^|\s)each\s*$/i.test(`${uom.name || ''} ${uom.shortName || ''}`));
            return each ? [each] : this.uomList;
        }
        const allowed = new Set(item.compatibleUomIds.map(Number));
        return this.uomList.filter(uom => allowed.has(Number(uom.uomId)));
    }

    save(): void {
        this.submitted = true;
        this.errorMessage = '';
        if (!this.isValid() || !this.shipmentId) return;

        this.saving = true;
        const request = this.source === 'CONTAINER'
            ? this.containerRequest()
            : this.payloadRequest();
        const call = this.source === 'CONTAINER'
            ? this.createContainer(request as ShipmentContainerManifestCommand)
            : this.savePayload(request as ShipmentPayloadLineCommand);

        call.pipe(finalize(() => this.saving = false)).subscribe({
            next: result => {
                this.visible = false;
                this.changed.emit(result);
                const detail = result.warehouseDocumentNumber
                    ? `${result.warehouseDocumentType === 'TRANSFER' ? 'Transfer' : 'Dispatch'} ${result.warehouseDocumentNumber} created.`
                    : result.containerCase ? 'Container Lifecycle case created.' : 'Payload line saved.';
                this.messageService.add({ severity: 'success', summary: 'Saved', detail });
            },
            error: error => this.errorMessage = this.formatError(error)
        });
    }

    isWarehouse(): boolean { return this.source === 'WAREHOUSE_ITEM'; }
    isContainer(): boolean { return this.source === 'CONTAINER'; }
    isEditing(): boolean { return !!this.editing?.shipmentLineId; }

    private open(): void {
        this.submitted = false;
        this.errorMessage = '';
        this.warehouseUomCache = new WeakMap();
        this.visible = true;
        this.loadWarehouseItems();
        this.loadJourneyTemplates();
    }

    isValid(): boolean {
        if (this.isContainer()) return true;
        if (!this.form.quantity || this.form.quantity <= 0 || !this.form.uom) return false;
        if (this.isWarehouse() && this.form.warehouseItem?.tenantUomOptions?.length
            && !this.form.warehouseItem.tenantUomOptions.some(option => option.tenantUomId === this.tenantUomId(this.form.uom))
            && !(this.editing && !this.editing.tenantUomId
                && this.form.warehouseItem.compatibleUomIds?.includes(Number(this.form.uom?.uomId)))) return false;
        return this.isWarehouse() ? !!this.form.warehouseItem || !!this.editing?.inventoryItemId : !!this.form.itemType;
    }

    private payloadRequest(): ShipmentPayloadLineCommand {
        const warehouse = this.form.warehouseItem;
        const payload = new PayloadItemView();
        Object.assign(payload, this.editing || {});
        payload.itemType = this.form.itemType;
        payload.itemDescription = this.form.description?.trim() || warehouse?.itemDescription || warehouse?.itemName;
        payload.itemDesciption = payload.itemDescription;
        payload.itemQuantity = Object.assign(new QuantityView(), {
            quantity: this.isWarehouse() && this.tenantUomId(this.form.uom) != null ? undefined : this.form.quantity,
            uom: this.isWarehouse() && this.tenantUomId(this.form.uom) != null ? undefined : this.form.uom
        });
        payload.enteredQuantity = this.isWarehouse() && this.tenantUomId(this.form.uom) != null
            ? this.form.quantity : undefined;
        payload.itemWeight = this.form.itemWeight;
        payload.weightUnit = this.form.weightUnit;
        if (this.isWarehouse()) {
            payload.tenantUomId = this.tenantUomId(this.form.uom);
            payload.inventoryItemId = warehouse?.inventoryItemId ?? this.editing?.inventoryItemId;
            payload.warehouseItemNumber = warehouse?.itemNumber ?? this.editing?.warehouseItemNumber;
            payload.skuNumber = warehouse?.skuNumber ?? this.editing?.skuNumber;
            payload.customerItemNumber = warehouse?.customerItemNumber ?? this.editing?.customerItemNumber;
            payload.warehouseItemName = warehouse?.itemName ?? this.editing?.warehouseItemName;
            payload.trackingMode = warehouse?.trackingMode ?? this.editing?.trackingMode;
            payload.sourceWarehouseId = warehouse?.defaultWarehouseId ?? this.editing?.sourceWarehouseId;
            payload.conditionId = warehouse?.defaultConditionId ?? this.editing?.conditionId;
        }
        return { lineSource: this.source as 'GENERAL_CARGO' | 'WAREHOUSE_ITEM', payloadItem: payload };
    }

    private containerRequest(): ShipmentContainerManifestCommand {
        return {
            containerNumber: this.trim(this.form.containerNumber),
            isoEquipmentType: this.trim(this.form.isoEquipmentType),
            sealNumber: this.trim(this.form.sealNumber),
            bookingNumber: this.trim(this.form.bookingNumber),
            billOfLadingNumber: this.trim(this.form.billOfLadingNumber),
            houseBillNumber: this.trim(this.form.houseBillNumber),
            customsDeclarationNumber: this.trim(this.form.customsDeclarationNumber),
            journeyTemplateId: this.form.journeyTemplateId
        };
    }

    private savePayload(request: ShipmentPayloadLineCommand) {
        if (this.deliveryId) {
            return this.isEditing()
                ? this.shipmentService.updateDeliveryPayloadLine(this.shipmentId!, this.deliveryId, this.editing!.shipmentLineId!, request)
                : this.shipmentService.createDeliveryPayloadLine(this.shipmentId!, this.deliveryId, request);
        }
        return this.isEditing()
            ? this.shipmentService.updateShipmentPayloadLine(this.shipmentId!, this.editing!.shipmentLineId!, request)
            : this.shipmentService.createShipmentPayloadLine(this.shipmentId!, request);
    }

    private createContainer(request: ShipmentContainerManifestCommand) {
        return this.deliveryId
            ? this.shipmentService.createDeliveryContainerManifest(this.shipmentId!, this.deliveryId, request)
            : this.shipmentService.createShipmentContainerManifest(this.shipmentId!, request);
    }

    private delete(item: PayloadItemView): void {
        const request = this.deliveryId
            ? this.shipmentService.deleteDeliveryPayloadLine(this.shipmentId!, this.deliveryId, item.shipmentLineId!)
            : this.shipmentService.deleteShipmentPayloadLine(this.shipmentId!, item.shipmentLineId!);
        this.saving = true;
        request.pipe(finalize(() => this.saving = false)).subscribe({
            next: result => {
                this.changed.emit(result);
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Payload line deleted.' });
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Payload not deleted', detail: this.formatError(error) })
        });
    }

    private loadWarehouseItems(): void {
        if (!this.shipment || this.warehouseItemsLoading) return;
        this.warehouseItemsLoading = true;
        this.intakeService.searchWarehouseItems({
            crmClientId: this.shipment.crmClientId,
            crmAccountId: this.shipment.crmAccountId
        }).pipe(finalize(() => this.warehouseItemsLoading = false)).subscribe({
            next: items => {
                this.warehouseItems = items || [];
                if (this.editing?.inventoryItemId && !this.form.warehouseItem) {
                    this.form.warehouseItem = this.warehouseItems.find(item => item.inventoryItemId === this.editing?.inventoryItemId);
                    if (this.editing.tenantUomId) {
                        this.form.uom = this.warehouseUoms().find(uom => this.tenantUomId(uom) === this.editing?.tenantUomId);
                    }
                }
            },
            error: () => this.warehouseItems = []
        });
    }

    private loadJourneyTemplates(): void {
        if (this.journeyTemplatesLoading || this.journeyTemplates.length) return;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        this.journeyTemplatesLoading = true;
        this.containerTrackingService.findJourneyTemplates(companyId)
            .pipe(finalize(() => this.journeyTemplatesLoading = false))
            .subscribe({
                next: templates => this.journeyTemplates = (templates || [])
                    .filter((view: ContainerJourneyTemplateView) => view.template?.id != null)
                    .map((view: ContainerJourneyTemplateView) => ({
                        value: view.template!.id!,
                        label: [view.template!.templateName, view.template!.templateCode].filter(Boolean).join(' | ')
                    })),
                error: () => this.journeyTemplates = []
            });
    }

    private matchPayloadType(type?: PayloadType): PayloadType | undefined {
        return this.payloadTypes.find(option => Number(option.typeId) === Number(type?.typeId)) || type;
    }

    private matchUom(uom?: UOM): UOM | undefined {
        return this.uomList.find(option => Number(option.uomId) === Number(uom?.uomId)) || uom;
    }

    private tenantUomId(uom?: UOM): number | undefined {
        return (uom as UOM & { tenantUomId?: number } | undefined)?.tenantUomId;
    }

    private matchWeight(weight?: Weight): Weight | undefined {
        return this.weightUnits.find(option => Number(option.weightId) === Number(weight?.weightId)) || weight;
    }

    private payloadDescription(item: PayloadItemView): string {
        return item.warehouseItemName || item.itemDescription || item.itemDesciption || item.itemType?.name || 'this payload line';
    }

    private trim(value?: string): string | undefined {
        return value?.trim() || undefined;
    }

    private formatError(error: any): string {
        const code = error?.error?.code;
        const message = error?.error?.message || error?.message || 'Could not complete the payload action.';
        return code ? `${code}: ${message}` : message;
    }
}
